import React from 'react';
import { Link } from 'react-router-dom';
import { 
  UserGroupIcon,
  HeartIcon,
  TrophyIcon,
  AcademicCapIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import { leadership } from '../data/content';
import SEO from '../components/SEO';

const About: React.FC = () => {
  const stats = [
    { number: '76+', label: 'JAHRE AN ERFAHRUNG' },
    { number: '50+', label: 'MITGLIEDER' },
    { number: '6+', label: 'ZERTIFIZIERTE TRAINER' },
    { number: '2', label: 'TRAININGSPLÄTZE' }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-background-dark">
      <SEO
        title="Über uns"
        description="HSV Berlin-Adlershof e.V. - Hundesportverein seit 1948 in Treptow-Köpenick. Geschichte, Werte, Trainer-Team und moderne Trainingsflächen im Grünen."
        url="/about"
        breadcrumbs={[
          { name: 'Startseite', url: '/' },
          { name: 'Über uns', url: '/about' }
        ]}
      />
      {/* Hero Section with Full Width Image */}
      <section className="relative h-[400px] overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/images/about/hero-team.jpg" 
            alt="HSV Team" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="relative h-full flex items-center">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="max-w-3xl">
              <h1 className="text-2xl md:text-5xl font-black text-white mb-4 md:mb-6 uppercase">
                Engagierter Hundesportverein in Berlin Treptow-Köpenick
              </h1>
              <p className="text-base md:text-xl text-white/90">
                Seit 1948 fördern wir die harmonische Beziehung zwischen Mensch und Hund durch vielseitige sportliche Aktivitäten.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section - Narrow */}
      <section className="py-10 md:py-20 bg-white dark:bg-background-dark">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="mb-10 md:mb-16">
              <h2 className="text-2xl md:text-4xl font-bold mb-5 md:mb-8 text-neutral-900 dark:text-neutral-100 uppercase">
                Über den<br />
                Hundesportverein<br />
                Berlin-Adlershof
              </h2>
              <div className="space-y-4 text-neutral-600 dark:text-neutral-400 text-base md:text-lg">
                <p>
                  Der Hundesportverein Berlin-Adlershof e.V. (HSV-Adlershof) ist mehr als nur ein Ort für Hundesport – er ist eine Gemeinschaft mit tiefen historischen Wurzeln und einem starken Gemeinschaftsgefühl. Gegründet im Jahr 1948 als Teil einer Initiative für Dienst- und Gebrauchshunde, entwickelte sich der Verein aus bescheidenen Anfängen zu einer angesehenen Sportstätte in Berlin. Ursprünglich entstand der Trainingsplatz am Birkenwäldchen, wo über die Jahre hinweg unzählige Mitglieder ehrenamtlich Zeit und Energie investierten, um den Verein zu dem zu machen, was er heute ist: ein lebendiges Zentrum für Hundesport, Gemeinschaft und Austausch.
                </p>
                <Link to="/geschichte" className="inline-block text-primary-600 dark:text-primary-400 hover:underline">
                  Mehr Lesen...
                </Link>
              </div>
            </div>

            {/* Vereinshaus Image - Narrow */}
            <div className="mb-10 md:mb-16">
              <img
                src="/images/facilities/clubhouse.jpg"
                alt="Vereinshaus"
                className="w-full rounded-lg shadow-xl"
              />
            </div>
          </div>

          {/* Mission and Activities Grid */}
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6 md:gap-8 mb-10 md:mb-16">
              {/* Unsere Geschichte */}
              <div className="space-y-4">
                <img 
                  src="/images/about/club-history.jpg" 
                  alt="Hunde Gruppe" 
                  className="w-full h-48 object-cover rounded-lg"
                />
                <h3 className="text-xl font-bold text-neutral-900 dark:text-neutral-100 uppercase">
                  Unsere Geschichte
                </h3>
                <ul className="space-y-2 text-sm text-neutral-600 dark:text-neutral-400">
                  <li>– 1948: Sektion Dienst- und Gebrauchshunde startet im Birkenwäldchen.</li>
                  <li>– 1952: Hürdenparcours für Hundeausbildung in Betrieb, erster Teil des Vereinsheims entsteht.</li>
                  <li>– 1953: Umgestaltung des Sportheims (heutige Gaststätte) durch das Ferienheimlassen.</li>
                  <li>Sanitäreinrichtungen werden gebaut.</li>
                  <li>– 2010/1990: Gründung des „Schutz-und Gebrauchshundesportvereins der SGSV e.V.".</li>
                  <li>– 02.05.1992: Namensänderung in „Hundesportverein Berlin-Adlershof e.V.".</li>
                  <li>– Seit 01.01.1993: DVG-Verbandsmitglied</li>
                  <li>– 11.05.2006: Anerkennung der Erziehungstätigkeit durch die Senatsverwaltung für Bildung, Jugend und Sport.</li>
                </ul>
                <Link to="/geschichte" className="inline-block text-primary-600 dark:text-primary-400 hover:underline text-sm">
                  Lese mehr...
                </Link>
              </div>

              {/* Unsere Mission */}
              <div className="space-y-4">
                <img 
                  src="/images/about/rewards.jpg" 
                  alt="Leckerlis" 
                  className="w-full h-48 object-cover rounded-lg"
                />
                <h3 className="text-xl font-bold text-neutral-900 dark:text-neutral-100 uppercase">
                  Unsere Mission
                </h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  Unsere Mission ist es, den Hundesport als Weg zur Förderung von Harmonie und Vertrauen zwischen Mensch und Hund zu gestalten. Durch Trainingsangebote, die sowohl auf die Verbesserung der Fitness als auch auf die Stärkung des Teamgeistes abzielen, schaffen wir eine Umgebung, in der Hund und Halter gemeinsam wachsen können.
                </p>
              </div>

              {/* Unser Ansatz */}
              <div className="space-y-4">
                <img 
                  src="/images/about/achievements.jpg" 
                  alt="Medaillen" 
                  className="w-full h-48 object-cover rounded-lg"
                />
                <h3 className="text-xl font-bold text-neutral-900 dark:text-neutral-100 uppercase">
                  Unser Ansatz
                </h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  Beim HSV-Adlershof steht die enge Beziehung zwischen Hund und Mensch im Mittelpunkt. Unsere erfahrenen Trainer legen Wert auf eine sanfte und spielerische Herangehensweise, die auf die individuellen Bedürfnisse von Hund und Halter abgestimmt ist. Mit Disziplinen wie Begleithundeprüfungen, Hürdernisparcours und Rally-Obedience bieten wir eine abwechslungsreiche Auswahl an Sportarten, die Freude und Erfolgserlebnisse verbinden.
                </p>
              </div>
            </div>
          </div>

          {/* Stats Section */}
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 py-8 md:py-12 border-t border-neutral-200 dark:border-neutral-700">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl md:text-5xl font-bold text-primary-600 dark:text-primary-400 mb-1 md:mb-2">
                    {stat.number}
                  </div>
                  <div className="text-xs uppercase tracking-wider text-neutral-600 dark:text-neutral-400">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Facilities Section - Wide */}
      <section className="py-10 md:py-20 bg-green-50 dark:bg-green-900/20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-neutral-900 dark:text-neutral-100 uppercase">
              Unsere Einrichtungen
            </h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-400 max-w-5xl mx-auto">
              Unsere Anlagen umfassen großzügige Trainingsflächen im Grünen, die Raum für vielfältige Aktivitäten bieten:
            </p>
          </div>

          <div className="max-w-5xl mx-auto space-y-4 mb-12">
            <div className="flex items-start space-x-3">
              <span className="text-primary-600 dark:text-primary-400 font-bold">→</span>
              <p className="text-neutral-700 dark:text-neutral-300">
                <strong>MODERNE TRAININGSFLÄCHEN:</strong> AUSREICHEND PLATZ FÜR VERSCHIEDENE HUNDESPORTARTEN.
              </p>
            </div>
            <div className="flex items-start space-x-3">
              <span className="text-primary-600 dark:text-primary-400 font-bold">→</span>
              <p className="text-neutral-700 dark:text-neutral-300">
                <strong>ERFAHRENE TRAINER:</strong> QUALIFIZIERTE ANLEITUNG FÜR ALLE MITGLIEDER.
              </p>
            </div>
            <div className="flex items-start space-x-3">
              <span className="text-primary-600 dark:text-primary-400 font-bold">→</span>
              <p className="text-neutral-700 dark:text-neutral-300">
                <strong>VIELFÄLTIGE SPORTANGEBOTE:</strong> BEGLEITHUNDEPRÜFUNGEN, RALLY-OBEDIENCE UND MEHR
              </p>
            </div>
            <div className="flex items-start space-x-3">
              <span className="text-primary-600 dark:text-primary-400 font-bold">→</span>
              <p className="text-neutral-700 dark:text-neutral-300">
                <strong>REGELMÄßIGE VERANSTALTUNGEN:</strong> VOM KOSSO-POKAL BIS HIN ZU SPASSTURNIEREN.
              </p>
            </div>
          </div>

          {/* Training Ground Images */}
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <div className="relative overflow-hidden rounded-lg shadow-xl">
              <img 
                src="/images/facilities/training-field-1.jpg" 
                alt="Trainingsplatz 1" 
                className="w-full h-[300px] object-cover"
              />
              <div className="absolute bottom-4 left-4 bg-white/90 dark:bg-black/90 px-3 py-1 rounded">
                <span className="text-sm font-medium">1. Trainingsplatz</span>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-lg shadow-xl">
              <img 
                src="/images/facilities/training-field-2.jpg" 
                alt="Trainingsplatz 2" 
                className="w-full h-[300px] object-cover"
              />
              <div className="absolute bottom-4 left-4 bg-white/90 dark:bg-black/90 px-3 py-1 rounded">
                <span className="text-sm font-medium">2. Trainingsplatz</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vorstand Section - Full Width */}
      <section className="py-10 md:py-20 bg-white dark:bg-background-dark">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-4xl font-bold mb-4 md:mb-6 text-neutral-900 dark:text-neutral-100 uppercase">
              Unser Vorstand
            </h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-400">
              Engagierte Menschen, die unseren Verein leiten
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {leadership.map((person: any, index) => (
              <div key={index} className="group">
                <div className="card overflow-hidden hover:shadow-xl transition-all duration-300 h-full">
                  {/* Image Container */}
                  <div className="relative h-48 overflow-hidden bg-primary-100 dark:bg-primary-900/20">
                    {person.image.includes('ui-avatars') ? (
                      <div className="w-full h-full flex items-center justify-center bg-primary-500">
                        <UserGroupIcon className="w-20 h-20 text-white" />
                      </div>
                    ) : (
                      <img 
                        src={person.image}
                        alt={person.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    )}
                    <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-1 text-neutral-900 dark:text-neutral-100">
                      {person.name}
                    </h3>
                    <p className="text-primary-600 dark:text-primary-400 font-semibold text-sm mb-3">
                      {person.position}
                    </p>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
                      {person.description}
                    </p>
                    {person.email && (
                      <a 
                        href={`mailto:${person.email}`}
                        className="inline-flex items-center text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors group/link"
                      >
                        <EnvelopeIcon className="w-4 h-4 mr-1.5" />
                        <span className="group-hover/link:underline">Kontakt</span>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;